import { Box, Card, CardContent, Chip, Stack, Typography } from "@mui/material";
import ProofImages from "./ProofImages";

const formatDateTime = (date) =>
  date
    ? new Date(date).toLocaleString(undefined, { timeZoneName: "short" })
    : null;

// Mirrors CANCELLATION_REASONS in the backend's utility/orderCancellation.js.
const REASON_LABELS = {
  ITEM_UNAVAILABLE: "Item no longer available",
  CHANGED_MIND: "Changed their mind",
  CANT_AGREE_ON_DETAILS: "Couldn't agree on the details",
  DELIVERY_NOT_POSSIBLE: "Delivery or pickup not possible",
  OTHER: "Other",
};

const REQUEST_STATUS_COLORS = {
  PENDING: "warning",
  ACCEPTED: "success",
  DECLINED: "default",
  WITHDRAWN: "default",
  EXPIRED: "default",
  SUPERSEDED: "default",
};

const COMPLETED_VIA_LABELS = {
  AGREEMENT: "Agreed before any payment was confirmed",
  REFUND_CONFIRMED: "Buyer confirmed the refund arrived",
  ADMIN: "Closed by an admin after a dispute",
};

const partyLabel = (role) => (role === "SELLER" ? "Seller" : "Buyer");

// Admin view of an order's mutual cancellation: every request to call it off
// and how it ended, and for a paid order, where the refund stands and the
// seller's refund proof.
const OrderCancellation = ({ order }) => {
  const cancellation = order.cancellation;
  const requests = cancellation?.requests || [];
  if (!requests.length) return null;
  const progress = order.cancellationProgress;

  let summary = null;
  if (cancellation.completedAt) {
    summary = {
      color: "success",
      text: `Cancelled by agreement on ${formatDateTime(cancellation.completedAt)}. ${COMPLETED_VIA_LABELS[cancellation.completedVia] || ""}`,
    };
  } else if (cancellation.agreedAt) {
    summary = {
      color: "warning",
      text: `Both agreed to cancel on ${formatDateTime(cancellation.agreedAt)}. The order was paid, so it stays open until the buyer confirms the refund.${cancellation.refundMarkedAt ? ` Seller marked the refund sent on ${formatDateTime(cancellation.refundMarkedAt)}.` : " The seller hasn't marked the refund sent yet."}`,
    };
  } else if (progress?.status === "REQUESTED") {
    summary = {
      color: "info",
      text: "A request to call off this order is open.",
    };
  }

  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardContent>
        <Typography fontWeight={600} mb={1}>
          Calling off the order
        </Typography>
        {summary && (
          <Box
            mb={1.5}
            p={1}
            bgcolor="grey.100"
            borderLeft={3}
            borderColor={`${summary.color}.main`}
            borderRadius={1}
          >
            <Typography variant="body2">{summary.text}</Typography>
          </Box>
        )}
        <Stack spacing={1.5} mb={1.5}>
          {requests.map((request, i) => (
            <Box key={request._id || i}>
              <Stack direction="row" spacing={1} alignItems="center" mb={0.25}>
                <Typography variant="body2" fontWeight={600}>
                  {partyLabel(request.requestedBy)} asked
                </Typography>
                <Chip
                  size="small"
                  label={request.status}
                  color={REQUEST_STATUS_COLORS[request.status] || "default"}
                />
              </Stack>
              <Typography variant="body2">
                {REASON_LABELS[request.reason] || request.reason}
                {request.note ? `: "${request.note}"` : ""}
              </Typography>
              {request.responseNote && (
                <Typography variant="body2" color="text.secondary">
                  {partyLabel(
                    request.requestedBy === "SELLER" ? "USER" : "SELLER",
                  )}{" "}
                  replied: "{request.responseNote}"
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary">
                Asked {formatDateTime(request.createdAt)} while{" "}
                {request.orderStatusAtRequest}, payment{" "}
                {request.paymentStatusAtRequest || "unknown"}
                {request.respondedAt
                  ? ` · ${request.status.toLowerCase()} ${formatDateTime(request.respondedAt)}`
                  : ""}
              </Typography>
            </Box>
          ))}
        </Stack>
        <ProofImages
          title="Seller's refund proof"
          images={cancellation.refundProofImages}
        />
      </CardContent>
    </Card>
  );
};

export default OrderCancellation;
